import { useEffect, useState } from "react";
import { Close } from "../../../../assets/icons/Close";
import { MFButton } from "../../../../common/components/MFButton";
import { defaultCalculsFilters, FINISHED, IN_PROGRESS } from "../../constants";
import { ColorDiv } from "../../../../layout/ColorDiv";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { MFCheckbox } from "../../../../common/components/MFCheckbox";
import { AlertCircle } from "../../../../assets/icons/AlertCircle";
import { Check } from "../../../../assets/icons/Check";

export const CalculsFilters = ({
  closeCalculsFiltersSection,
  isFilterSectionOpen,
  localConfigurator,
  aggregatedFamilyProducts,
  familyProducts,
  getAggregatedFamilyProducts,
  getFamilyProducts,
  loadAllUserFolders,
  allFolders,
  folderId,
  filters,
  updateFilters,
}) => {
  const [localFilters, setLocalFilters] = useState(
    filters || defaultCalculsFilters,
  );
  const [isOpen, setIsOpen] = useState(isFilterSectionOpen);
  const [filteredFamilyProducts, setFilteredFamilyProducts] = useState([]);
  const [isStatusesFilterOpen, setIsStatusesFilterOpen] = useState(false);
  const [isProductFamiliesFilterOpen, setIsProductFamiliesFilterOpen] =
    useState(false);
  const [isFoldersFilterOpen, setIsFoldersFilterOpen] = useState(false);
  const [isUpdatedAtFilterOpen, setIsUpdatedAtFilterOpen] = useState(false);
  const [isResultFilterOpen, setIsResultFilterOpen] = useState(false);
  const updatedAtFilterNotComplete =
    (localFilters?.updatedAt?.$gte && !localFilters?.updatedAt?.$lte) ||
    (!localFilters?.updatedAt?.$gte && localFilters?.updatedAt?.$lte);
  const hasResultMin =
    localFilters?.result?.$gte !== undefined &&
    localFilters?.result?.$gte !== null;
  const hasResultMax =
    localFilters?.result?.$lte !== undefined &&
    localFilters?.result?.$lte !== null;
  const resultFilterNotComplete =
    (hasResultMin && !hasResultMax) || (!hasResultMin && hasResultMax);
  const isResultFilterInfosClosed = localStorage.getItem(
    "isResultFilterInfosClosed",
  );

  useEffect(() => {
    getAggregatedFamilyProducts();
    getFamilyProducts();
    loadAllUserFolders();
  }, []); // eslint-disable-line

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  //populate filteredFamilyProducts with family products and their children that are in aggregatedFamilyProducts
  useEffect(() => {
    if (familyProducts && aggregatedFamilyProducts) {
      setFilteredFamilyProducts(
        familyProducts?.flatMap(
          (fp) =>
            fp.children?.flatMap((fpc) =>
              aggregatedFamilyProducts?.some(
                (afp) => afp.id === fpc._id && afp.isVisible,
              )
                ? [
                    fpc,
                    ...(fpc.children?.filter((child) =>
                      aggregatedFamilyProducts?.some(
                        (afp) => afp.id === child._id && afp.isVisible,
                      ),
                    ) || []),
                  ]
                : [],
            ) || [],
        ) || [],
      );
    }
  }, [familyProducts, aggregatedFamilyProducts]);

  //used to delay animation at opening or else it doesn't work
  useEffect(() => {
    setTimeout(() => {
      setIsOpen(isFilterSectionOpen);
    }, 0);
  }, [isFilterSectionOpen]);

  const handleApplyFilters = () => {
    updateFilters(localFilters, folderId);
    closeCalculsFiltersSection();
  };

  const handleResetFilters = () => {
    updateFilters(defaultCalculsFilters, folderId);
    setLocalFilters(defaultCalculsFilters);
    closeCalculsFiltersSection();
  };

  const handleChangeStatuses = (status) => {
    setLocalFilters({
      ...localFilters,
      statuses: localFilters.statuses.includes(status)
        ? localFilters.statuses.filter((s) => s !== status)
        : [...localFilters.statuses, status],
    });
  };

  const handleChangeProductFamilies = (familyId, text) => {
    setLocalFilters({
      ...localFilters,
      productFamilies: localFilters.productFamilies.some(
        (f) => f.id === familyId,
      )
        ? localFilters.productFamilies.filter((f) => f.id !== familyId)
        : [...localFilters.productFamilies, { id: familyId, text }],
    });
  };

  const handleChangeFolders = (folderId, text) => {
    setLocalFilters({
      ...localFilters,
      folders: localFilters.folders.some((f) => f.id === folderId)
        ? localFilters.folders.filter((f) => f.id !== folderId)
        : [...localFilters.folders, { id: folderId, text }],
    });
  };

  const handleChangeUpdatedAt = (e, type) => {
    setLocalFilters({
      ...localFilters,
      updatedAt: {
        ...localFilters.updatedAt,
        [type]: e.target.value ? e.target.value : undefined,
      },
    });
  };

  const handleChangeResult = (e, type) => {
    setLocalFilters({
      ...localFilters,
      result: {
        ...localFilters.result,
        [type]: e.target.value ? Number(e.target.value) : undefined,
      },
    });
  };

  const closeResultFilterInfos = () => {
    localStorage.setItem("isResultFilterInfosClosed", true);
    // force re-render to apply the change in localStorage
    setLocalFilters((prev) => ({ ...prev }));
  };

  const colors = {
    headerColor:
      localConfigurator && localConfigurator.color
        ? localConfigurator.color
        : null,
    textPrimaryBlack: "#2F2B3DE5",
    errorColor: "#FF4C51",
    interactionColor:
      localConfigurator &&
      localConfigurator.theme &&
      localConfigurator.theme.colors
        ? localConfigurator.theme.colors.primaryText
        : null,
  };

  return (
    isFilterSectionOpen && (
      <ColorDiv {...colors}>
        <div
          className="filters-section-container"
          onClick={closeCalculsFiltersSection}
        >
          <div style={{ position: "relative" }}>
            <div
              className={`filters-container d-flex flex-column ${isOpen ? "open" : "closed"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Filtrer par</span>
                <button
                  className="no-style"
                  onClick={closeCalculsFiltersSection}
                >
                  <Close size={24} outlined />
                </button>
              </div>
              <div style={{ flex: "1 1 auto" }}>
                {/* ---- STATUS ---- */}
                <div
                  className="filter-div"
                  onClick={() => setIsStatusesFilterOpen(!isStatusesFilterOpen)}
                >
                  <span>Statuts</span>
                  <div className="d-flex align-items-center">
                    {localFilters.statuses.length > 0 && (
                      <div className="panel-filter-badge bg-interaction-clear text-interaction d-flex align-items-center justify-content-center">
                        {localFilters.statuses.length}
                      </div>
                    )}
                    <ChevronDown
                      size={20}
                      className={`chevron ${isStatusesFilterOpen ? "open" : ""}`}
                    />
                  </div>
                </div>
                {isStatusesFilterOpen && (
                  <div className="checkboxes-container">
                    <MFCheckbox
                      checked={localFilters.statuses?.includes(IN_PROGRESS)}
                      onChange={(e) => handleChangeStatuses(IN_PROGRESS)}
                      label="En cours"
                    />
                    <MFCheckbox
                      checked={localFilters.statuses?.includes(FINISHED)}
                      onChange={(e) => handleChangeStatuses(FINISHED)}
                      label="Finalisé"
                    />
                  </div>
                )}

                {/* ---- PRODUCT FAMILIES ---- */}
                <div
                  className="filter-div"
                  onClick={() =>
                    setIsProductFamiliesFilterOpen(!isProductFamiliesFilterOpen)
                  }
                >
                  <span>Famille de produits</span>
                  <div className="d-flex align-items-center">
                    {localFilters.productFamilies.length > 0 && (
                      <div className="panel-filter-badge bg-interaction-clear text-interaction d-flex align-items-center justify-content-center">
                        {localFilters.productFamilies.length}
                      </div>
                    )}
                    <ChevronDown
                      size={20}
                      className={`chevron ${isProductFamiliesFilterOpen ? "open" : ""}`}
                    />
                  </div>
                </div>
                {isProductFamiliesFilterOpen && (
                  <div className="checkboxes-container">
                    {filteredFamilyProducts.map((fp, index) => (
                      <MFCheckbox
                        key={`product-family-filter-${index}`}
                        checked={localFilters.productFamilies?.some(
                          (f) => f.id === fp._id,
                        )}
                        onChange={(e) =>
                          handleChangeProductFamilies(fp._id, fp.label)
                        }
                        label={fp.shortLabel || fp.label || ""}
                      />
                    ))}
                  </div>
                )}

                {/* ---- FOLDERS ---- */}
                {!folderId && (
                  <>
                    <div
                      className="filter-div"
                      onClick={() =>
                        setIsFoldersFilterOpen(!isFoldersFilterOpen)
                      }
                    >
                      <span>Dossiers</span>
                      <div className="d-flex align-items-center">
                        {localFilters.folders.length > 0 && (
                          <div className="panel-filter-badge bg-interaction-clear text-interaction d-flex align-items-center justify-content-center">
                            {localFilters.folders.length}
                          </div>
                        )}
                        <ChevronDown
                          size={20}
                          className={`chevron ${isFoldersFilterOpen ? "open" : ""}`}
                        />
                      </div>
                    </div>
                    {isFoldersFilterOpen && (
                      <div className="checkboxes-container">
                        {allFolders.map((folder, index) => (
                          <MFCheckbox
                            key={`folder-filter-${index}`}
                            checked={localFilters.folders?.some(
                              (f) => f.id === folder._id,
                            )}
                            onChange={(e) =>
                              handleChangeFolders(folder._id, folder.label)
                            }
                            label={folder.label}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
                {/* ---- UPDATED AT ---- */}
                <div
                  className="filter-div"
                  onClick={() =>
                    setIsUpdatedAtFilterOpen(!isUpdatedAtFilterOpen)
                  }
                >
                  <span>Date de modification</span>
                  <div className="d-flex align-items-center">
                    {localFilters.updatedAt?.$gte &&
                      localFilters.updatedAt?.$lte && (
                        <div className="panel-filter-badge check bg-interaction-clear text-interaction d-flex align-items-center justify-content-center">
                          <Check size={20} className="text-interaction" />
                        </div>
                      )}
                    <ChevronDown
                      size={20}
                      className={`chevron ${isUpdatedAtFilterOpen ? "open" : ""}`}
                    />
                  </div>
                </div>
                {isUpdatedAtFilterOpen && (
                  <div className="d-flex align-items-center">
                    <label style={{ fontSize: 13 }} className="mr-1 w-100">
                      De
                      <input
                        className="form-control"
                        onChange={(e) => handleChangeUpdatedAt(e, "$gte")}
                        value={localFilters.updatedAt?.$gte}
                        onClick={(e) => e.target.select()}
                        type="date"
                      />
                    </label>
                    <label style={{ fontSize: 13 }} className="w-100">
                      À
                      <input
                        className="form-control"
                        onChange={(e) => handleChangeUpdatedAt(e, "$lte")}
                        value={localFilters.updatedAt?.$lte}
                        onClick={(e) => e.target.select()}
                        type="date"
                      />
                    </label>
                  </div>
                )}
                {updatedAtFilterNotComplete && (
                  <span className="text-error" style={{ fontSize: 12 }}>
                    Veuillez renseigner une date de début et une date de fin
                    pour appliquer le filtre de date de modification
                  </span>
                )}
                {/* ---- RESULT ---- */}
                <div
                  className="filter-div"
                  onClick={() => setIsResultFilterOpen(!isResultFilterOpen)}
                >
                  <span>Résultat</span>
                  <div className="d-flex align-items-center">
                    {hasResultMin && hasResultMax && (
                      <div className="panel-filter-badge check bg-interaction-clear text-interaction d-flex align-items-center justify-content-center">
                        <Check size={20} className="text-interaction" />
                      </div>
                    )}
                    <ChevronDown
                      size={20}
                      className={`chevron ${isResultFilterOpen ? "open" : ""}`}
                    />
                  </div>
                </div>
                {isResultFilterOpen && (
                  <>
                    <div className="d-flex align-items-center">
                      <label style={{ fontSize: 13 }} className="mr-1 w-100">
                        De
                        <input
                          className="form-control"
                          onChange={(e) => handleChangeResult(e, "$gte")}
                          value={localFilters.result?.$gte}
                          onClick={(e) => e.target.select()}
                          type="number"
                        />
                      </label>
                      <label style={{ fontSize: 13 }} className="w-100">
                        À
                        <input
                          className="form-control"
                          onChange={(e) => handleChangeResult(e, "$lte")}
                          value={localFilters.result?.$lte}
                          onClick={(e) => e.target.select()}
                          type="number"
                        />
                      </label>
                    </div>
                    {!isResultFilterInfosClosed && (
                      <div className="info-div text-interaction mt-1 bg-interaction-clear">
                        <div className="info-logo bg-interaction">
                          <AlertCircle size={22} className="text-white" />
                        </div>
                        <div className="d-flex flex-column">
                          <span className="title">Valeurs acceptées</span>
                          Vous pouvez également renseigner des valeurs
                          négatives.
                        </div>
                        <button
                          className="no-style close-button"
                          style={{ height: "fit-content" }}
                          onClick={closeResultFilterInfos}
                        >
                          <Close size={20} className="text-interaction" />
                        </button>
                      </div>
                    )}
                  </>
                )}
                {resultFilterNotComplete && (
                  <span className="text-error" style={{ fontSize: 12 }}>
                    Veuillez renseigner un résultat minimal et un résultat
                    maximal pour appliquer le filtre de résultat
                  </span>
                )}
                {/* -------------- */}
              </div>
              <div>
                <MFButton
                  huge
                  height={48}
                  onClick={handleApplyFilters}
                  className="w-100"
                  style={{ marginBottom: 10 }}
                  disabled={
                    updatedAtFilterNotComplete ||
                    resultFilterNotComplete ||
                    JSON.stringify(localFilters) ===
                      JSON.stringify(defaultCalculsFilters)
                  }
                >
                  Appliquer
                </MFButton>
                <MFButton
                  huge
                  height={48}
                  onClick={handleResetFilters}
                  outlined
                  className="w-100"
                >
                  Réinitialiser les filtres
                </MFButton>
              </div>
            </div>
          </div>
        </div>
      </ColorDiv>
    )
  );
};
